<template>
  <Dialog
    v-model:visible="visibleModel"
    class="col-12"
    position="top"
    :modal="true"
    :closable="true"
    :header="$t('dialog.barcodeform.title')"
    :dismissable-mask="true"
  >
    <template #header>
      <div class="grid">
        <h3>{{ $t('dialog.barcodeform.title') }}</h3>
      </div>
    </template>

    <div>
      <form
        class="grid"
        @submit.prevent="create"
      >
        <BarcodeRangeInput
          v-model:start="barcode.start"
          v-model:number="barcode.number"
          v-model:valid="rangeValid"
          class="col-12 lg:col-6"
          start-class="md:col-6"
          number-class="md:col-6"
        />

        <div class="col-12 md:col-6 lg:col-3">
          <label
            for="stakeholder"
            class="required-input"
          >{{ $t('entity.stakeholder.name') }}</label>
          <Dropdown
            id="stakeholder"
            v-model="barcode.stakeholder"
            :disabled="!!stakeholder"
            :options="stakeholderOptions"
            option-label="name"
            class="w-full"
          />
          <div v-if="v$.barcode.stakeholder.$error">
            <p
              v-for="error in v$.barcode.stakeholder.$errors"
              :key="error.$uid"
              class="error-msg"
            >
              {{ $t(error.$message) }}
            </p>
          </div>
        </div>

        <div
          v-show="barcode.stakeholder"
          class="w-full"
        >
          <Paginator
            v-model:first="actualPage"
            v-model:rows="lineIdFilter.pageable.size"
            :total-records="totalLinesCount"
            @page="onLineIdPageChange($event)"
          />
          <LineIdAdvancedFilter
            v-model:stakeholder-id="lineIdFilter.filters.stakeholderId.value"
            v-model:mainVenue="lineIdFilter.filters.mainVenue.value"
            v-model:spectrumService="lineIdFilter.filters.spectrumService.value"
            v-model:tuningRangeFrom="lineIdFilter.filters.tuningRangeFrom.value"
            v-model:tuningRangeTo="lineIdFilter.filters.tuningRangeTo.value"
            v-model:duplexHigh="lineIdFilter.filters.duplexHigh.value"
            v-model:duplexLow="lineIdFilter.filters.duplexLow.value"
            :stakeholder="barcode.stakeholder"
            @validate="getLines"
            @reset="resetFilter"
            :replace-licence-period="true"
          />

          <LineIdTable
            v-model:sortField="lineIdFilter.pageable.sortField"
            v-model:sortOrder="lineIdFilter.pageable.sortOrder"
            v-model:filters="lineIdFilter.filters"
            v-model:rows="lineIdFilter.pageable.size"
            v-model:selection="selectedLines"
            :value="lineIds"
            :total-records="totalLinesCount"
            selection-mode="multiple"
            :show-stakeholder-id="false"
            show-channel-bandwidth
            show-spectrum-service
            show-dates
            :show-delete-button="false"
            :show-detail-button="false"
            :show-select-button="true"
            @sort="getLines"
            @filter="getLines"
            @page="onLineIdPageChange($event)"
            @select="selectedLines = $event.value"
            :metaKeySelection="false"
            dataKey="id"
          >
            <template #before>
              <Column
                selection-mode="multiple"
                :exportable="false"
                header-style="width: 3rem"
              />
            </template>
          </LineIdTable>

          <Paginator
            v-model:first="actualPage"
            v-model:rows="lineIdFilter.pageable.size"
            :total-records="totalLinesCount"
            @page="onLineIdPageChange($event)"
          />
        </div>

        <Message
          v-if="errors.length > 0"
          class="col-12"
          severity="error"
          :closable="false"
        >
          <ul class="list-none m-0 p-0">
            <li
              v-for="error of errors"
              :key="error"
            >
              {{ $t(error) }}
            </li>
          </ul>
        </Message>
        <div class="w-full text-right mt-2 p-4">
          <Button
            :label="$t('action.cancel')"
            type="button"
            icon="pi pi-times"
            class="p-button-rounded p-button-secondary p-button-outlined mr-2"
            @click="visibleModel = false"
          />
          <Button
            :label="$t('action.crud.create')"
            type="submit"
            icon="pi pi-plus"
            class="p-button-rounded p-button-outlined float-right"
            :disabled="!rangeValid || v$.$error || isSaving"
          />
        </div>
      </form>
    </div>
  </Dialog>
</template>
<script>
import Dialog from "primevue/dialog";
import {reactive} from "vue";
import BarcodeRangeInput from "@/components/barcodes/BarcodeRangeInput.vue";
import Message from "primevue/message";
import BarcodeRangeCreateRequest from "@/models/dto/requests/BarcodeRangeCreateRequest";
import {useVuelidate} from "@vuelidate/core";
import {required} from "@/i18n/i18nValidators";
import Button from "primevue/button";
import LineIdTable from "@/components/lineId/LineIdTable.vue";
import useLineIdFilter from "@/composables/filters/useLineIdFilter";
import LineIdAdvancedFilter from "@/components/lineId/LineIdAdvancedFilter.vue";
import Dropdown from "primevue/dropdown";
import Paginator from "primevue/paginator";
import Column from "primevue/column";

export default {
  name: "BarcodeCreateDialog",
  components: {
    LineIdAdvancedFilter,
    LineIdTable,
    BarcodeRangeInput,
    Dialog,
    Message,
    Dropdown,
    Button,
    Paginator,
    Column
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    stakeholder: {
      type: Object,
      default: null
    },
    immediateTagging: {
      type: Object,
      default: null
    },
    appointment: {
      type: Object,
      default: null
    }
  },
  emits: ["update:visible", "create"],
  setup() {
    return {
      v$: useVuelidate(),
      lineIdFilter: reactive(useLineIdFilter())
    }
  },
  data() {
    return {
      isSaving: false,
      errors: [],
      rangeValid: true,
      barcode: {
        start: 0,
        number: 1,
        stakeholder: this.stakeholder
      },
      lineIds: [],
      totalLinesCount: 0,
      selectedLines: [],
      stakeholderOptions: [],
      actualPage: 1,
    }
  },
  computed: {
    visibleModel: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      }
    }
  },
  validations() {
    return {
      barcode: {
        stakeholder: {required}
      }
    }
  },
  async mounted() {
    this.lineIdFilter.pageable.size = this.$store.getters['setting/getSizePerPageLineId'];
    this.$watch('barcode.stakeholder', (value) => {
      this.lineIdFilter.filters.stakeholder.value = value?.id;
      this.getLines();
    })

    if (this.stakeholder) {
      this.stakeholderOptions = [this.stakeholder];
      this.lineIdFilter.filters.stakeholder.value = this.stakeholder.id;
      this.getLines();
    }else{
      this.stakeholderOptions = await this.$stakeHolderService.getAll();
    }


  },
  methods: {
    async getLines() {
      if (!this.barcode.stakeholder) {
        return;
      }
      const params = this.lineIdFilter.getQueryParams(this.$store.getters['setting/getSizePerPageLineId']);
      const {content, totalElements} = await this.$lineIdService.fetchLinesPagingAndSorting(params);
      this.lineIds = content;
      this.totalLinesCount = totalElements;
    },
    onLineIdPageChange(event) {
      this.lineIdFilter.pageable.page = event.page;
      this.getLines();
      this.actualPage = event.page * this.lineIdFilter.pageable.size;
    },
    resetFilter() {
      this.lineIdFilter.reset();
      this.lineIdFilter.filters.stakeholder.value = this.barcode.stakeholder?.id;
      this.getLines();
    },

    resetForm() {
      this.barcode.start = 0;
      this.barcode.number = 1;
      this.barcode.stakeholder = this.stakeholder;
      this.selectedLines = [];
      this.v$.$reset();
      this.errors = [];
    },

    async create() {
      this.errors = [];
      await this.v$.$validate();
      if (this.v$.$error) {
        return;
      }

      try {
        this.isSaving = true;
        const request = new BarcodeRangeCreateRequest({
          start: this.barcode.start,
          number: this.barcode.number,
          stakeholderId: this.barcode.stakeholder?.id,
          lineIds: this.selectedLines.map(line => line.id),
          immediateTaggingId: this.immediateTagging?.id,
          appointmentId: this.appointment?.id
        });
        await this.$barcodeService.create(request);
        this.$emit("create");
        this.$emit("update:visible", false);
        this.$emitter.emit("toast", {
          severity: "success",
          message: "dialog.barcodeform.toast.success",
        });
        this.resetForm();
      } catch (e) {
        this.errors = e.response.data;
      } finally {
        this.isSaving = false;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
#dialog {
  min-height: 500px;
}
</style>
